import React, { useRef, useState } from 'react';

import Input from '../ui/Input';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { useSpecialCharPicker } from '../ui/SpecialCharDialog';
import { parseApiError } from '../../utils/apiError';
import CategoryParentPicker from './CategoryParentPicker';

// Create/edit form for a category. `initial` seeds edit mode; `onSubmit(body)`
// performs the mutation (create or update) and may throw an axios error, which
// is mapped to inline field errors via parseApiError. `categories` is the full
// flat admin list, needed by the parent picker.
export default function CategoryForm({ initial, categories = [], onSubmit, onDone, onCancel }) {
  const [values, setValues] = useState({
    name: initial?.name ?? '',
    displayOrder: initial?.displayOrder ?? 0,
    active: initial?.active ?? true,
    depositPercent: initial?.depositPercent ?? 0,
    localPickupOnly: initial?.localPickupOnly ?? false,
    parentId: initial?.parentId ?? null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  // Last known caret position in the name field, so the special-character
  // button (which moves focus into its own dialog) still knows where to
  // insert — the input's own selectionStart can't be trusted once focus
  // has left it. See TextBlockLayer.js for the same pattern.
  const nameCursorRef = useRef(null);
  const trackNameCursor = (e) => {
    nameCursorRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
  };
  const { openPicker, dialog: specialCharDialog } = useSpecialCharPicker();

  const insertIntoName = (char) => {
    setValues((v) => {
      const text = v.name || '';
      const { start = text.length, end = text.length } = nameCursorRef.current || {};
      const nextPos = start + char.length;
      nameCursorRef.current = { start: nextPos, end: nextPos };
      return { ...v, name: text.slice(0, start) + char + text.slice(end) };
    });
  };

  // Local pickup requires a deposit — mirrors the server's own validation so
  // the error surfaces before a round-trip.
  const depositPercentNum = Number(values.depositPercent) || 0;
  const pickupNeedsDeposit = values.localPickupOnly && depositPercentNum === 0;

  // The chosen parent's own `isLeaf` tells us whether this save is the parent's
  // first subcategory — the server moves any products it currently holds
  // directly onto this new child in that case (addSubcategory).
  const parentCategory = values.parentId ? categories.find((c) => c._id === values.parentId) : null;
  const parentIsUntouchedLeaf = !!(parentCategory && parentCategory.isLeaf);

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    if (pickupNeedsDeposit) {
      setFieldErrors({ depositPercent: 'Required when local pickup only is checked' });
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        name: values.name,
        displayOrder: Number(values.displayOrder) || 0,
        active: values.active,
        depositPercent: depositPercentNum,
        localPickupOnly: values.localPickupOnly,
        parentId: values.parentId || null,
      });
      onDone?.();
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
      setSubmitting(false);
    }
  };

  return (
    <form className="ss-form" onSubmit={submit} noValidate>
      {formError && <p className="ss-form__error" role="alert">{formError}</p>}

      <Input
        label="Name"
        value={values.name}
        onChange={(e) => {
          setField('name')(e);
          trackNameCursor(e);
        }}
        onSelect={trackNameCursor}
        error={fieldErrors.name || fieldErrors.slug}
        required
        autoFocus
        endAdornment={
          <button
            type="button"
            className="ss-input-reveal"
            title="Insert special character"
            aria-label="Insert special character"
            // Prevents the browser's default mousedown focus-steal so the
            // name field's caret position isn't lost when this is clicked.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openPicker(insertIntoName)}
          >
            &#937;
          </button>
        }
      />
      <Input
        label="Display order"
        type="number"
        value={values.displayOrder}
        onChange={setField('displayOrder')}
        error={fieldErrors.displayOrder}
      />

      <div className="ss-field">
        <span className="ss-label">Parent category</span>
        <CategoryParentPicker
          categories={categories}
          value={values.parentId}
          onChange={(parentId) => setValues((v) => ({ ...v, parentId }))}
          excludeId={initial?._id}
        />
        {parentIsUntouchedLeaf && (
          <p className="ss-field__hint">
            {parentCategory.name} currently holds products directly — saving will move them onto this
            new subcategory.
          </p>
        )}
        {fieldErrors.parentId && <p className="ss-field__error">{fieldErrors.parentId}</p>}
      </div>

      <label className="ss-check">
        <input
          type="checkbox"
          checked={values.localPickupOnly}
          onChange={(e) => setValues((v) => ({ ...v, localPickupOnly: e.target.checked }))}
        />
        <span>Local pickup only</span>
      </label>
      {pickupNeedsDeposit && (
        <p className="ss-field__error" style={{ marginTop: '-8px' }}>
          Local pickup only requires a deposit percent above 0.
        </p>
      )}

      <Input
        label="Deposit percent"
        type="number"
        min="0"
        max="100"
        value={values.depositPercent}
        onChange={setField('depositPercent')}
        error={fieldErrors.depositPercent}
      />
      <p style={{ color: 'var(--ss-brown-hover)', fontSize: '0.85rem', marginTop: '-8px' }}>
        0 = pay in full at checkout. 1–100 = only that percent of a product's price is
        charged now; the rest is tracked as balance due later.
      </p>

      <label className="ss-check">
        <input
          type="checkbox"
          checked={values.active}
          onChange={(e) => setValues((v) => ({ ...v, active: e.target.checked }))}
        />
        <span>Active (visible in the storefront)</span>
      </label>

      <div className="ss-form__actions">
        <Button type="submit" disabled={submitting}>
          {submitting ? <Spinner label="Saving" /> : 'Save category'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        )}
      </div>
      {specialCharDialog}
    </form>
  );
}
